import { HeaderCallToAction } from './hooks/useHeaderCallToAction/enum';
import type { PageViewed } from './telemetry';
import { Page } from './telemetry/page';

const External = {
  ANALYTICS: {
    path: '/analytics',
  },
  CONTACT: { path: 'https://hover.to/contact' },
  SUPPORT_EMAIL: { path: 'mailto:support@hover.to' },
  CAREERS: { path: '/jobs' },
  ESTIMATION_BANNER: { path: 'https://hover.to/estimate-and-close/' },
  ESTIMATION_CREATE: {
    path: '/ehi/#/estimator/questions/select_templates?jobId=:jobId',
  },
  ESTIMATION_DETAIL: {
    path: '/ehi/#/estimator/estimates/:estimateGroupId/details?jobId=:jobId',
  },
  ESTIMATION_LEARN_MORE: {
    path: 'https://help.hover.to/en/collections/3046380-transform',
  },
  HOMEOWNER_RENDERINGS: { path: '/homeowner-renderings' },
  JAMES_HARDIE: {
    path: 'https://www.jameshardie.com/request-hover-design-tool/',
  },
  PRICING: { path: '/pricing' },
  PRIVACY_POLICY: { path: 'https://hover.to/privacy' },
  PRODUCT: { path: '/product' },
  RENDERINGS: { path: '/renderings' },
  RENDERINGS_NEW: { path: '/homeowner-designs' },
  RENDERINGS_LEARN_MORE_PRO: {
    path: '/design-images',
  },
  RENDERINGS_LEARN_MORE_HOMEOWNER: {
    path: '/homeowner-designs',
  },
  SOCIAL_FACEBOOK: { path: 'https://www.facebook.com/hover3d' },
  SOCIAL_PINTEREST: { path: 'https://www.pinterest.com/hover3d' },
  SOCIAL_TWITTER: { path: 'https://twitter.com/hover3d' },
  SOCIAL_YOUTUBE: { path: 'https://www.youtube.com/user/Hover3D' },
  SOCIAL_INSTAGRAM: { path: 'https://www.instagram.com/hover3d/' },
  MANAGE_TEAM: { path: '/ui/#/team/:orgId' },
  MATERIAL_LIST: { path: '/material-list' },
  PROJECT_LIST: { path: '/ehi/#/estimates' },
  ADMIN: { path: '/admin/' },
  TERMS_OF_SERVICE: { path: 'https://hover.to/terms_of_use' },
  MANAGE_WORKFLOWS: { path: '/ehi/#/workflows' },
  MODEL_LICENSE: {
    path: 'https://huggingface.co/spaces/CompVis/stable-diffusion-license',
  },
  MODEL_CREATOR: {
    path: 'https://civitai.com/user/Photographer/images?sort=Newest',
  },
  PROPOSALS_MANAGE: { path: 'https://hover.to/ehi/#/workflows/proposal' },
  PROPOSALS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/5535283-proposal-management-customization',
  },
  ORDERING_LEARN_MORE: {
    path: 'https://hover.to/lp/hover-sdr/?utm_source=7014N000000XDgiQAG&campaignMedium=Website&campaignSource=Direct%20Ordering',
  },
  NEW_CONSTRUCTION_JOB_PAUSED: {
    path: 'https://help.hover.to/en/articles/6460725-why-was-my-new-construction-job-paused',
  },
  BLUEPRINT_TERMS: {
    path: '/ui/#/hvr/blueprint_terms',
  },
  CHECKLISTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/6619282-inspection-checklist',
  },
  CHECKLISTS_CUSTOMIZE_TEMPLATES: {
    path: '/ehi/#/workflows/inspection_templates',
  },
  MEASUREMENTS_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/?utm_source=7014N000002CRuAQAW',
  },
  ADVANCED_EXPORTS_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/3d-exports',
  },
  ESX_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/esx-exports',
  },
  JOB_ASSIGNMENTS: {
    path: '/ui/#/orgs/:orgId/jobs/:jobId/assign',
  },
  ORG_ASSIGNMENTS: {
    path: '/ui/#/orgs/:orgId/jobs/:jobId/accesses',
  },
  RAILS_ADMIN: {
    path: '/admin/jobs/:jobId',
  },
  JOIN_MOSAIC: {
    path: `https://tap.joinmosaic.com/?ref=hover19&hvr=:jobId`,
  },
  JOIN_MOSAIC_DEMO: {
    path: `https://tap.demo.joinmosaic.com/?ref=hover19&hvr=:jobId`,
  },
  EHI_CREATE_MATERIAL_LIST: {
    path: `/ehi/#/project/:jobId/scope?orgId=:orgId&materialListCreate=true`,
  },
  EHI_CREATE_MATERIAL_LIST_MIXED: {
    path: `/ehi/#/project_estimator/select_templates?jobId=:jobId`,
  },
  EHI_VIEW_MATERIAL_LIST: {
    path: '/ehi/#/project/:jobId/scope?orgId=:orgId',
  },
  EHI_CREATE_ESTIMATE: {
    path: `/ehi/#/estimator/questions/select_templates?jobId=:jobId`,
  },
  EHI_VIEW_CHECKLIST: {
    path: '/ehi/#/estimator/inspection_checklists/:checklistId/?orgId=:orgId&jobId=:jobId',
  },
  NEW_CONSTRUCTION_ADD_FILES: {
    path: '/ui/#/property/:jobId',
  },
  MEASUREMENTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/collections/2633342-getting-started-with-hover',
  },
  MEASUREMENTS_PROCESSING_BLUEPRINTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/6383819-how-to-upload-a-blueprint',
  },
  PLAN: {
    path: '/settings/plan/edit',
  },
  SETTINGS: {
    path: '/settings/profile',
  },
  MANOWAR: {
    path: `/orders/:jobManowarIdentifier`,
  },
  BILLING: {
    path: '/settings/billing',
  },
  PAYMENTS: {
    path: '/settings/payment-method',
  },
} as const;

type MetadataKeys = (keyof PageViewed)[];

export interface RouteDescriptor {
  path: string;
  page?: Page;
  headerCallToAction?: HeaderCallToAction;
  metadata?: MetadataKeys;
}

const designStudioMetadataKeys: MetadataKeys = ['job_id', 'partner_id'];
const homeownerAppMetadataKeys: MetadataKeys = ['session_id'];

export const facebookPromotionRoutes = {
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_20: {
    path: '/signup/professional/facebook-20',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_40: {
    path: '/signup/professional/facebook-40',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_60: {
    path: '/signup/professional/facebook-60',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_75: {
    path: '/signup/professional/facebook-75',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
} as const;

const Route = {
  WEB_UPLOAD: {
    path: '/web-upload',
    page: Page.WEB_UPLOAD,
  },
  SIGNUP_PROFESSIONAL_ACTIVATE: {
    path: '/signup/professional/activate/:token',
    page: Page.SIGNUP_PROFESSIONAL_ACTIVATE__INPUT,
  },
  DESIGN_STUDIO: {
    path: '/design-studio',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_SHARE_VIEW: {
    path: '/design-studio/shared/:id',
    page: Page.DESIGN_STUDIO_SHARE_VIEW,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_STUDIO_PRO_VIEW: {
    path: '/design-studio/:id/user/:userId',
    page: Page.DESIGN_STUDIO_PRO_VIEW,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_STUDIO_GUEST: {
    path: '/design-studio/guest',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_PREVIEW: {
    path: '/design-studio/preview',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_EDIT: {
    path: '/design-studio/:id',
    page: Page.DESIGN_STUDIO,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_ORDERS: {
    path: '/design-orders',
    page: Page.DESIGN_ORDERS,
  },
  DESIGN_ORDERS_SUMMARY: {
    path: '/design-orders/summary/:rendering_version_id',
    page: Page.DESIGN_ORDERS_SUMMARY,
  },
  DESIGN_ORDERS_HOMEOWNER: {
    path: '/design-orders/homeowner',
    page: Page.DESIGN_ORDERS_HOMEOWNER,
  },
  DESIGN_ORDERS_HOMEOWNER_EDIT: {
    path: '/design-orders/homeowner/edit/:rendering_version_id',
    page: Page.DESIGN_ORDERS_HOMEOWNER,
  },
  DESIGN_ORDERS_HOMEOWNER_EDIT_STEP: {
    path: '/design-orders/homeowner/edit/:rendering_version_id/step/:step_number',
    page: Page.DESIGN_ORDERS_HOMEOWNER_EDIT_STEP,
  },
  DESIGN_ORDERS_HOMEOWNER_EDIT_SUMMARY: {
    path: '/design-orders/homeowner/edit/:rendering_version_id/summary',
    page: Page.DESIGN_ORDERS_HOMEOWNER_EDIT_SUMMARY,
  },
  DESIGN_ORDERS_HOMEOWNER_EDIT_END: {
    path: '/design-orders/homeowner/edit/:rendering_version_id/end',
    page: Page.DESIGN_ORDERS_HOMEOWNER_EDIT_END,
  },
  DESIGN_ORDERS_HOMEOWNER_REQUEST: {
    path: '/design-orders/homeowner/requests/:rendering_version_id',
    page: Page.DESIGN_ORDERS_HOMEOWNER_REQUEST,
  },
  DESIGN_ORDERS_HOMEOWNER_REQUEST_STEP: {
    path: '/design-orders/homeowner/requests/:rendering_version_id/step/:step_number',
    page: Page.DESIGN_ORDERS_HOMEOWNER_REQUEST_STEP,
  },
  DESIGN_ORDERS_HOMEOWNER_REQUEST_SUMMARY: {
    path: '/design-orders/homeowner/requests/:rendering_version_id/summary',
    page: Page.DESIGN_ORDERS_HOMEOWNER_REQUEST_SUMMARY,
  },
  DESIGN_ORDERS_HOMEOWNER_REQUEST_END: {
    path: '/design-orders/homeowner/requests/:rendering_version_id/end',
    page: Page.DESIGN_ORDERS_HOMEOWNER_REQUEST_END,
  },
  DESIGN_ORDERS_PRO: {
    path: '/design-orders/pro',
    page: Page.DESIGN_ORDERS_PRO,
  },
  DESIGN_ORDERS_PRO_EDIT: {
    path: '/design-orders/pro/edit/:rendering_version_id',
    page: Page.DESIGN_ORDERS_PRO,
  },
  DESIGN_ORDERS_PRO_EDIT_STEP: {
    path: '/design-orders/pro/edit/:rendering_version_id/step/:step_number',
    page: Page.DESIGN_ORDERS_PRO_EDIT_STEP,
  },
  DESIGN_ORDERS_PRO_REQUEST: {
    path: '/design-orders/pro/requests/:rendering_version_id',
    page: Page.DESIGN_ORDERS_PRO_REQUEST,
  },
  DESIGN_ORDERS_PRO_REQUEST_INTRO: {
    path: '/design-orders/pro/requests/:rendering_version_id/intro',
    page: Page.DESIGN_ORDERS_PRO_REQUEST_INTRO,
  },
  DESIGN_ORDERS_PRO_REQUEST_STEP: {
    path: '/design-orders/pro/requests/:rendering_version_id/step/:step_number',
    page: Page.DESIGN_ORDERS_PRO_REQUEST_STEP,
  },
  DESIGN_ORDERS_PRO_REQUEST_SUMMARY: {
    path: '/design-orders/pro/requests/:rendering_version_id/summary',
    page: Page.DESIGN_ORDERS_PRO_REQUEST_SUMMARY,
  },
  DESIGN_ORDERS_PRO_REQUEST_END: {
    path: '/design-orders/pro/requests/:rendering_version_id/end',
    page: Page.DESIGN_ORDERS_PRO_REQUEST_END,
  },
  DESIGN_ORDERS_PRO_EDIT_SUMMARY: {
    path: '/design-orders/pro/edit/:rendering_version_id/summary',
    page: Page.DESIGN_ORDERS_PRO_EDIT_SUMMARY,
  },
  DESIGN_ORDERS_PRO_EDIT_END: {
    path: '/design-orders/pro/edit/:rendering_version_id/end',
    page: Page.DESIGN_ORDERS_PRO_EDIT_END,
  },
  VIRTUAL_WALKTHROUGH: {
    path: '/virtual-walkthrough/:id',
    page: Page.VIRTUAL_WALKTHROUGH,
  },
  VIRTUAL_WALKTHROUGH_REFERRER: {
    path: '/virtual-walkthrough/:id/?referrer=property-details',
  },
  DESIGN_PRO_EDIT: {
    path: '/3d/:id',
    page: Page.DESIGN_PRO,
    metadata: designStudioMetadataKeys,
  },
  AI_DESIGNER_SHARE: {
    path: '/aidesigner/share',
    page: Page.HOMEOWNER_APP_SHARE,
    metadata: homeownerAppMetadataKeys,
  },
  AI_DESIGNER: {
    path: '/aidesigner',
    page: Page.HOMEOWNER_APP_HOME,
    metadata: homeownerAppMetadataKeys,
  },
  AI_DESIGNER_INSPIRATIONS: {
    path: '/aidesigner/:aiOriginalImageId/inspirations',
    page: Page.HOMEOWNER_APP_ORIGINAL,
    metadata: homeownerAppMetadataKeys,
  },
  BULK_CAPTURE_INVITE: {
    path: '/wr/bulk-capture-invite',
    page: Page.BULK_CAPTURE_INVITE,
  },
  HOMEOWNER_APP_HOME: {
    path: '/designer',
    page: Page.HOMEOWNER_APP_HOME,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_INTERSTITIAL_THREE_D_FUSION: {
    path: '/designer/interstitial/three_d_fusion/:jobId',
    page: Page.HOMEOWNER_APP_INTERSTITIAL_THREE_D_FUSION,
  },
  HOMEOWNER_APP_SAVED: {
    path: '/designer/saved',
    page: Page.HOMEOWNER_APP_SAVED,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_SAVED_DISPLAY_FULLSCREEN: {
    path: '/designer/saved/display/:aiDisplayImageId',
    page: Page.HOMEOWNER_APP_SAVED__DETAIL,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_PROJECT: {
    path: '/designer/projects/:projectId',
    page: Page.HOMEOWNER_APP_PROJECT,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN: {
    path: '/designer/projects/:projectId/display/:aiDisplayImageId',
    page: Page.HOMEOWNER_APP_PROJECT_DETAIL,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_PROJECTS_LIST: {
    path: '/designer/projects',
    page: Page.HOMEOWNER_APP_PROJECTS_LIST,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_NEW_PROJECT: {
    path: '/designer/projects/new',
    page: Page.HOMEOWNER_APP_NEW_PROJECT,
    metadata: homeownerAppMetadataKeys,
  },
  PROJECT_SHARE: {
    path: '/designer/share/:id',
    page: Page.LEAD_LANDING_PAGE,
  },
  PROJECT_SHARE_FULLSCREEN: {
    path: '/designer/share/:id/display/:aiDisplayImageId',
    page: Page.LEAD_LANDING_PAGE,
  },
  HOMEOWNER_APP_SHARE: {
    path: '/designer/share',
    page: Page.HOMEOWNER_APP_SHARE,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_CUSTOMIZE: {
    path: '/designer/edit/:aiDisplayImageId/:category?/:region?',
    page: Page.HOMEOWNER_APP_CUSTOMIZE,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_CUSTOMIZE_INSPIRE: {
    path: '/designer/edit/:aiDisplayImageId/inspire',
    page: Page.HOMEOWNER_APP_CUSTOMIZE,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_CUSTOMIZE_UPLOAD: {
    path: '/designer/edit/upload',
    page: Page.HOMEOWNER_APP_CUSTOMIZE,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_ORIGINAL: {
    path: '/designer/original/:aiOriginalImageId',
    page: Page.HOMEOWNER_APP_ORIGINAL,
    metadata: homeownerAppMetadataKeys,
  },
  HOMEOWNER_APP_ORIGINAL_DISPLAY_FULLSCREEN: {
    path: '/designer/original/:aiOriginalImageId/display/:aiDisplayImageId',
    page: Page.HOMEOWNER_APP_ORIGINAL_GENERATED__DETAIL,
    metadata: homeownerAppMetadataKeys,
  },
  INTERNAL: { path: '/i' },
  INTERNAL_PROFILE: { path: '/i/profile' },
  INTERNAL_LOGOUT: { path: '/i/logout' },
  LOGIN: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login',
    page: Page.LOGIN__INPUT,
  },
  LOGIN_BEACON: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login/beacon',
    page: Page.BEACON_LOGIN__INPUT,
  },
  LOGIN_BEACON_SUCCESS: {
    path: '/login/beacon/success',
    page: Page.BEACON_LOGIN_SUCCESS,
  },
  LOGIN_FORGOT: {
    path: '/login/forgot',
    page: Page.FORGOT_PASSWORD__INPUT,
  },
  LOGIN_FORGOT_SUCCESS: {
    path: '/login/forgot/success',
    page: Page.PASSWORD_EMAIL_SENT,
  },
  LOGIN_FORGOT_PASSWORD: { path: '/login/forgot-password' },
  LOGIN_SSO: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login/sso',
    page: Page.SSO_LOGIN__INPUT,
  },
  LOGIN_SSO_FAILURE: {
    path: '/login/sso/failure',
    page: Page.SSO_LOGIN_FAILURE,
  },
  LOGIN_SSO_SUCCESS: {
    path: '/login/sso/success',
    page: Page.SSO_LOGIN_SUCCESS,
  },
  NOT_FOUND: { path: '/404', page: Page.NOT_FOUND },
  LOGIN_RESET: {
    path: '/login/reset/:token',
    page: Page.RESET_PASSWORD__INPUT,
  },
  LOGIN_RESET_SUCCESS: {
    path: '/login/reset/success',
    page: Page.RESET_PASSWORD_SUCCESS,
  },
  LOGIN_RESET_PASSWORD: { path: '/login/reset-password/:token' },
  PROPERTIES: {
    path: '/wr/properties',
    page: Page.PROPERTIES,
  },
  GROUPS: {
    path: '/wr/groups/:groupId',
    page: Page.GROUPS,
  },
  PROPERTY_DETAILS: {
    path: '/wr/properties/:jobId/:tabId(estimation|checklists|measurements|material-list|photos|proposal|design|members|inspections)?',
    page: Page.PROPERTY_DETAILS,
  },
  NEW_CONSTRUCTION_UPLOAD: {
    path: '/wr/new-construction/upload',
    page: Page.NEW_CONSTRUCTION_UPLOAD,
  },
  MANAGE_TEAMS: {
    path: '/wr/manage-teams',
    page: Page.MANAGE_TEAMS,
  },
  RENDERINGS_HUB: {
    path: '/wr/properties/:jobId/renderings/:renderingId',
    page: Page.RENDERINGS_HUB,
  },
  RENDERINGS_SHARED: {
    path: '/wr/renderings/shared/:shareToken',
    page: Page.RENDERINGS_SHARED,
  },
  RENDERINGS_USER_ACTIVATE: {
    path: '/wr/renderings/user/activate/:token',
    page: Page.RENDERINGS_USER_ACTIVATE,
  },
  RENDERINGS_INTERACTIVE: {
    path: '/wr/renderings/:renderingId/design/:renderingFileId',
    page: Page.RENDERINGS_INTERACTIVE,
  },
  INSPECTION: {
    path: '/wr/properties/:jobId/inspections/:inspectionId',
    page: Page.INSPECTION,
  },
  PAINT_MEASUREMENTS: {
    path: '/wr/measurements/:jobId/paint',
    page: Page.PAINT_MEASUREMENTS,
  },
  PAINT_MEASUREMENTS_SHARED: {
    path: '/wr/measurements/:jobId/paint/shared/:shareToken',
    page: Page.PAINT_MEASUREMENTS_SHARED,
  },
  HOMEOWNER_MEASUREMENTS: {
    path: '/designer/measurements/:jobId/homeowner',
    page: Page.HOMEOWNER_MEASUREMENTS,
  },
  PRO: {
    path: '/pro/:publicIdentifier/:orgDisplayName',
    page: Page.PRO_LANDING_PAGE,
  },
  EMBED: {
    path: '/designer/embed-container',
    page: Page.EMBED,
  },
  ROOT: { path: '/' },
  SIGN_UP: { path: '/signup', page: Page.SIGNUP__FORK },
  SIGNUP_MOBILE: {
    headerCallToAction: HeaderCallToAction.NONE,
    path: '/signup/mobile',
    page: Page.SIGNUP_MOBILE,
  },
  SIGN_UP_HOMEOWNER: {
    path: '/signup/homeowner',
    page: Page.HOMEOWNER_ACCOUNT__INPUT,
  },
  SIGN_UP_HOMEOWNER_DOWNLOAD: {
    path: '/signup/homeowner/download',
    page: Page.HOMEOWNER_DOWNLOAD__INPUT,
  },
  SIGN_UP_HOMEOWNER_DOWNLOAD_SUCCESS: {
    path: '/signup/homeowner/download/success',
    page: Page.HOMEOWNER_DOWNLOAD_SENT,
  },
  SIGN_UP_INSURANCE: {
    path: '/signup/insurance',
    page: Page.INSURANCE_ACCOUNT__INPUT,
  },
  SIGN_UP_INSURANCE_ORGANIZATION: {
    path: '/signup/insurance/organization',
    page: Page.INSURANCE_COMPLETE_PROFILE__INPUT,
  },
  SIGN_UP_INSURANCE_SURVEY: {
    path: '/signup/insurance/survey',
    page: Page.INSURANCE_SURVEY_PROFILE__INPUT,
  },
  SIGN_UP_PROFESSIONAL: {
    path: '/signup/professional',
    page: Page.PROFESSIONAL_ACCOUNT__INPUT,
  },
  ...facebookPromotionRoutes,
  SIGN_UP_PROFESSIONAL_SSO: {
    path: '/signup/professional/sso',
    page: Page.SSO_EMAIL__INPUT,
  },
  SIGN_UP_PROFESSIONAL_ORGANIZATION: {
    path: '/signup/professional/organization',
    page: Page.COMPLETE_PROFILE__INPUT,
  },
  SIGN_UP_PROFESSIONAL_UPGRADE: {
    path: '/signup/professional/upgrade',
    page: Page.UPGRADE__INPUT,
  },
  SIGN_UP_PROFESSIONAL_SURVEY: {
    path: '/signup/professional/survey',
    page: Page.SURVEY_PROFILE__INPUT,
  },
  SIGN_UP_ONBOARDING: {
    path: '/onboarding',
    page: Page.SIGN_UP_ONBOARDING,
  },
  SIGN_UP_ONBOARDING_STEP: {
    path: '/onboarding/:step(create|welcome|signup|link|email|login|ssoSuccess|ssoFailure|check|password|reset|company|description|usage|phone|download|start|homeowner|pro|insurance|personal-info|business-info|operation)',
    page: Page.SIGN_UP_ONBOARDING,
  },
  SOMETHING_WRONG: {
    path: '/something-wrong',
    page: Page.SOMETHING_WRONG,
  },
} as const satisfies Record<string, RouteDescriptor>;

export const HOMEOWNER_APP_ROOT_ROUTE = '/designer*';

const LOGIN_ROUTES = [
  Route.LOGIN.path,
  Route.LOGIN_SSO.path,
  Route.LOGIN_BEACON.path,
  Route.SIGN_UP_ONBOARDING.path,
];

// Getting back a requires authentication from the profile query causes a redirect to login.
// This array prevents this behavior from happening
// all genai should be behind authentication:
// cf. https://hoverinc.atlassian.net/wiki/spaces/EN/pages/3463479328/2024-04-17+Disallow+users+from+uploading+PII+unauthenticated
const LOGIN_REDIRECT_EXCLUSIONS = [
  Route.RENDERINGS_USER_ACTIVATE.path,
  Route.AI_DESIGNER.path,
  Route.AI_DESIGNER_SHARE.path,
  Route.HOMEOWNER_APP_HOME.path,
  Route.HOMEOWNER_APP_SAVED.path,
  Route.HOMEOWNER_APP_SHARE.path,
  Route.HOMEOWNER_APP_ORIGINAL.path,
  Route.PAINT_MEASUREMENTS_SHARED.path,
  Route.PRO.path,
  Route.EMBED.path,
  ...LOGIN_ROUTES,
];

export { generatePath } from './helpers/generatePath';
export { External, Route, LOGIN_ROUTES, LOGIN_REDIRECT_EXCLUSIONS };
